import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Icon } from './Icon';

/** Display dimensions for the bonus-side glyphs (pot + items). Sized
 *  to match the wallet's 20×20 coin so the pill reads as one row of
 *  evenly-pitched icons rather than a "big bonus / small wallet" mix. */
const BONUS_ICON_SIZE = 22;

/** How far the pill tightens to fit {@link BalancePillProps.maxWidth}, a
 *  step at a time: 1 closer spacing, 2 no level chip, 3 no streak chip, 4 the
 *  gems abbreviated like the coins. */
const FIT_STEPS = 4;

/**
 * Format a wallet count for the HUD: exact (comma-grouped) below 1,000,
 * then abbreviated (1.2K, 10K, 1.2M, 1B, ...) so big balances stay inside
 * the pill (producer 2026-06-10: "once over 1000 change coins to 1.2K").
 * Keeps one decimal under 100 of a unit (1.2K, 1.2M) and rounds to a whole
 * number at/above it (250K), bumping to the next unit when rounding would
 * overflow (999,960 → 1M, not 1000K).
 */
function abbreviateCount(n: number): string {
  if (!Number.isFinite(n) || Math.abs(n) < 1000) return n.toLocaleString();
  const sign = n < 0 ? '-' : '';
  let v = Math.abs(n);
  const suffixes = ['', 'K', 'M', 'B', 'T'];
  let tier = 0;
  while (v >= 1000 && tier < suffixes.length - 1) {
    v /= 1000;
    tier += 1;
  }
  let text = v >= 100 ? String(Math.round(v)) : (Math.round(v * 10) / 10).toString();
  if (text === '1000' && tier < suffixes.length - 1) {
    text = '1';
    tier += 1;
  }
  return `${sign}${text}${suffixes[tier]}`;
}

/**
 * Persistent wallet HUD — the dark capsule that shows the player's
 * coin balance, gem balance, login streak, and player level. Extracted
 * from the v2 map HUD so the same component can be mounted as a
 * top-right overlay across every scene (slot, wheel cast lobby, wheel
 * room) — per the slot-HUD redesign brief: "balance hud from the map
 * is top right — this should always be present for the player."
 *
 * <p>During a wheel match the pill grows a "bonus" cluster on its LEFT
 * edge — two status chips, separated from the wallet by a vertically-
 * centred dot divider. Producer brief: "[Bonus coin] 5 [Mushroom] 2
 * o [Coin] 1050 [Diamond] 24 [Fire] 2 [Up] 12". The bonus chip is
 * the running Reel-Race pot; the item chip is the total count of
 * collected items (single biome-themed glyph + count, NOT a tile per
 * unique item). Status-only — deploy happens in the wheel's STAKE
 * surface, not in the pill.
 *
 * <p>Pointer events are scoped — the whole pill has
 * {@code pointer-events: none} so taps fall through to the scene
 * below, EXCEPT for the gem button (which re-enables clicks for the
 * wallet-explainer CTA wired via {@code onTapGems}).
 */
export interface BalancePillBonusProps {
  /** Running Reel-Race pot for the local seat. Shown next to the
   *  pot glyph. */
  potCount: number;
  /** URL of the "bonus coin" glyph — distinct from the wallet coin
   *  so the player can tell the pot apart from their wallet balance.
   *  Typically the biome's bonus reel symbol (forest = leaf coin). */
  potIconUrl: string;
  /** Total count of bonus items collected. Single glyph + count —
   *  does NOT split into per-item tiles. */
  itemCount: number;
  /** URL of the item glyph. Typically a biome-themed pickup
   *  (forest = mushroom). */
  itemIconUrl: string;
}

/** Position-bridge payload — the live viewport-CSS-px centres of the coin
 *  and gem numerics, measured on the rendered pill and forwarded to the
 *  Pixi slot scene so its take-win / gem-drop animations land on the real
 *  chips no matter how the pill's layout shifts (streak chip toggling,
 *  value width, font load, viewport resize). Consumed by
 *  SlotHudComponent.setWalletAnchors via CanvasHost. */
export interface WalletAnchors {
  coin: { x: number; y: number };
  gem: { x: number; y: number };
}

export interface BalancePillProps {
  /** Wallet coin balance — displayed in the wallet cluster. */
  coinBalance: number;
  /** Wallet gem balance — the gem chip is clickable (see onTapGems). */
  gemBalance: number;
  /** Login streak. Rendered as a flame + N chip when > 0; hidden at 0. */
  streak: number;
  /** When false, suppress the streak chip even when streak > 0. The
   *  single-player slot HUD hides it (in-game noise) while the map /
   *  biome overview keeps it. Default true. Producer 2026-06-10. */
  showStreak?: boolean;
  /** Player level. Rendered as an "up-arrow N" chip. Falls back to 1 when 0. */
  level: number;
  /** Open the gem shop. Hides the cursor pointer when null but keeps
   *  the gem chip visible as a status read. */
  onTapGems?: (() => void) | null;
  /** Optional bonus cluster rendered to the LEFT of the wallet with a
   *  vertically-centred dot divider between the two. Omit on screens
   *  with no bonus state (map, plain slot). */
  bonus?: BalancePillBonusProps | null;
  /** Compact mode — drops the streak + level chips and tightens the
   *  capsule to just coin + gem. Used on narrow layouts (the v2 map
   *  HUD on phones) where the full four-chip pill overflows the
   *  viewport alongside the episode card. Default false keeps the
   *  full pill everywhere else (slot, wheel cast lobby). */
  compact?: boolean;
  /** Neutral loading state — shown on a cold launch until the server
   *  wallet has been fetched + reconciled. Renders placeholder dashes
   *  for the coin + gem figures and suppresses the streak/level chips,
   *  so the pill never flashes the local placeholder balance (1000) and
   *  then visibly snap-corrects to the real server balance on the first
   *  spin. Cleared once the boot {@code useWalletSync} resolves. Default
   *  false. */
  loading?: boolean;
  /** Position bridge — reports the live coin + gem numeric centres (in
   *  viewport CSS px) whenever the pill's layout changes, so the Pixi
   *  take-win can fly to the real chips. Null when unmeasurable. */
  onWalletAnchors?: (anchors: WalletAnchors | null) => void;
  /** The most room the pill may take, in CSS px (from the round button at
   *  the top left to the screen's edge). When it would be wider it tightens
   *  its spacing, then drops its least important chips (level, then
   *  streak), then abbreviates the gems, until it fits. Null or omitted: no
   *  limit. */
  maxWidth?: number | null;
}

export function BalancePill({
  coinBalance,
  gemBalance,
  streak,
  showStreak = true,
  level,
  onTapGems,
  bonus,
  compact = false,
  loading = false,
  onWalletAnchors,
  maxWidth = null,
}: BalancePillProps) {
  // Neutral placeholder while the server wallet is still loading on a
  // cold launch — an em-dash keeps the pill's shape (icon + figure)
  // without committing to a number that would later snap-correct.
  const LOADING_GLYPH = '·';

  // ── Position bridge ──────────────────────────────────────────────
  // Report the coin + gem numerics' viewport centres so the Pixi slot
  // scene's take-win / gem-drop land on the real chips. One ResizeObserver
  // on the pill catches every layout shift (value width, streak/level/
  // bonus chips toggling, Fredoka load — all resize the pill) and a window
  // resize handler catches viewport moves; both far cheaper + steadier
  // than re-deriving the offsets scene-side.
  const rootRef = useRef<HTMLDivElement>(null);
  const coinRef = useRef<HTMLSpanElement>(null);
  const gemRef = useRef<HTMLSpanElement>(null);
  const lastAnchorsRef = useRef<string>('');

  // ── Fit ──────────────────────────────────────────────────────────
  // Measured, not guessed: the figures' widths and the font decide it.
  // After each render the pill steps tighter while it is wider than
  // maxWidth. It starts again from the full pill only when the room or
  // the pill's makeup changes (a chip comes or goes, a figure gains a
  // digit, the font arrives), so a coin count ticking up never re-fits
  // it every frame.
  const [fit, setFit] = useState(0);
  const [fontsIn, setFontsIn] = useState(false);
  useEffect(() => {
    let live = true;
    void document.fonts?.ready?.then(() => {
      if (live) setFontsIn(true);
    });
    return () => {
      live = false;
    };
  }, []);
  const coinText = loading ? LOADING_GLYPH : abbreviateCount(coinBalance);
  const fullGemText = loading ? LOADING_GLYPH : gemBalance.toLocaleString();
  const streakShown = !bonus && !compact && !loading && showStreak && streak > 0;
  const levelShown = !bonus && !compact && !loading;
  const fitKey = [
    maxWidth ?? '',
    coinText.length,
    fullGemText.length,
    streakShown ? String(streak).length : '',
    levelShown ? String(level || 1).length : '',
    bonus ? `${bonus.potCount}/${bonus.itemCount}`.length : '',
    compact,
    fontsIn,
  ].join('|');
  const fitForRef = useRef(fitKey);
  useLayoutEffect(() => {
    if (fitForRef.current !== fitKey) {
      fitForRef.current = fitKey;
      if (fit !== 0) {
        setFit(0);
        return;
      }
    }
    const el = rootRef.current;
    if (maxWidth === null || !el || fit >= FIT_STEPS) return;
    if (Math.max(el.offsetWidth, el.scrollWidth) > maxWidth) setFit(fit + 1);
    // The figures too: one can widen without gaining a digit.
  }, [fitKey, maxWidth, fit, coinText, fullGemText, streak, level]);
  const tight = compact || fit >= 1;
  const gemText = fit >= 4 && !loading ? abbreviateCount(gemBalance) : fullGemText;
  useLayoutEffect(() => {
    const report = onWalletAnchors;
    if (!report) return;
    const measure = () => {
      const c = coinRef.current?.getBoundingClientRect();
      const g = gemRef.current?.getBoundingClientRect();
      if (!c || !g || c.width === 0 || g.width === 0) {
        if (lastAnchorsRef.current !== 'null') {
          lastAnchorsRef.current = 'null';
          report(null);
        }
        return;
      }
      const next: WalletAnchors = {
        coin: {
          x: Math.round(c.left + c.width / 2),
          y: Math.round(c.top + c.height / 2),
        },
        gem: {
          x: Math.round(g.left + g.width / 2),
          y: Math.round(g.top + g.height / 2),
        },
      };
      // Dedupe — reporting re-renders the parent, which would otherwise
      // re-run this effect and loop. Only push when a centre moved.
      const key = `${next.coin.x},${next.coin.y},${next.gem.x},${next.gem.y}`;
      if (key === lastAnchorsRef.current) return;
      lastAnchorsRef.current = key;
      report(next);
    };
    measure();
    const ro =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro && rootRef.current) ro.observe(rootRef.current);
    window.addEventListener('resize', measure);
    // Fredoka loads async; its metrics shift the numerics once ready.
    void document.fonts?.ready?.then(measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [onWalletAnchors]);

  return (
    <div
      ref={rootRef}
      data-testid="balance-pill"
      style={{
        pointerEvents: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        // Never squeezed by a row it sits in: it fits itself (maxWidth).
        flexShrink: 0,
        gap: tight ? 10 : 14,
        padding: tight ? '8px 14px' : '8px 18px',
        borderRadius: 999,
        background: 'rgba(20,12,40,0.85)',
        color: '#fff',
        fontSize: 16,
        fontWeight: 800,
        height: 44,
        boxShadow:
          '0 4px 10px rgba(0,0,0,0.45), inset 0 -1px 2px rgba(255,255,255,0.12)',
        // Gold rim (2026-06-07) so the wallet pill joins the HUD/nav
        // pill family (PillButton) instead of its old white-ish rim.
        border: '2px solid rgba(255,228,138,0.4)',
        fontFamily: '"Fredoka", system-ui',
      }}
      aria-label="wallet"
      data-fit={fit}
    >
      {bonus ? (
        <>
          <BonusCluster
            potCount={bonus.potCount}
            potIconUrl={bonus.potIconUrl}
            itemCount={bonus.itemCount}
            itemIconUrl={bonus.itemIconUrl}
          />
          <DotDivider />
        </>
      ) : null}
      {/* Coin chip is tappable too (2026-06-07) — opens the same "Your
          wallet" sheet as the gem chip. */}
      <button
        type="button"
        onClick={onTapGems ?? undefined}
        style={{
          pointerEvents: 'auto',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          background: 'transparent',
          border: 'none',
          padding: 0,
          color: 'inherit',
          font: 'inherit',
          cursor: onTapGems ? 'pointer' : 'default',
        }}
        aria-label="Your wallet"
      >
        <Icon name="coin" size={20} alt="coins" />
        <span ref={coinRef}>{coinText}</span>
      </button>
      <button
        type="button"
        onClick={onTapGems ?? undefined}
        style={{
          pointerEvents: 'auto',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          background: 'transparent',
          border: 'none',
          padding: 0,
          color: 'inherit',
          font: 'inherit',
          cursor: onTapGems ? 'pointer' : 'default',
        }}
        aria-label="About gems and coins"
      >
        <Icon name="gem" size={18} alt="gems" />
        <span ref={gemRef}>{gemText}</span>
      </button>
      {/* Streak + level chips are wallet-progression chrome — useful
       *  on the map / slot HUD, but during a wheel match (bonus prop
       *  set) they're noise. Producer brief 2026-05-11: "in the bonus
       *  game we can hide [the streak and level]". */}
      {streakShown && fit < 3 && (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 13,
            color: '#FCE4F0',
          }}
          aria-label={`${streak}-day streak`}
        >
          <Icon name="flame" size={18} alt="" style={{ display: 'block' }} />
          {streak}
        </span>
      )}
      {levelShown && fit < 2 && (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 3,
            fontSize: 13,
            color: 'rgba(255,255,255,0.75)',
            letterSpacing: 0.3,
          }}
          aria-label={`Level ${level || 1}`}
        >
          {/* Up-arrow replaces the "Lvl" label (producer 2026-06-10) — a
              drawn glyph (not an emoji, per the no-emoji UI rule) tinted
              with the chip's muted level colour via currentColor. */}
          <svg
            width="10"
            height="12"
            viewBox="0 0 10 12"
            fill="none"
            aria-hidden
            style={{ display: 'block' }}
          >
            <path
              d="M5 1.6V10.4M1.6 5.2L5 1.6L8.4 5.2"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {level || 1}
        </span>
      )}
    </div>
  );
}

/** Bonus side — pot chip (running tokens) + items chip (single
 *  biome-themed glyph with a total count). Status-only; deploy moves
 *  to the wheel's STAKE surface. */
function BonusCluster({
  potCount,
  potIconUrl,
  itemCount,
  itemIconUrl,
}: BalancePillBonusProps) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
      }}
    >
      <PotChip count={potCount} iconUrl={potIconUrl} />
      <ItemsChip count={itemCount} iconUrl={itemIconUrl} />
    </span>
  );
}

/** Pot chip — bonus-coin glyph + running token count. Animates a "+N"
 *  burst whenever the pot grows so the player sees coins land in real
 *  time. Multiple bursts can stack briefly; each fades over ~900ms. */
function PotChip({ count, iconUrl }: { count: number; iconUrl: string }) {
  const prevCountRef = useRef(count);
  const [bursts, setBursts] = useState<{ id: number; delta: number }[]>([]);
  const nextIdRef = useRef(0);
  useEffect(() => {
    const prev = prevCountRef.current;
    prevCountRef.current = count;
    if (count <= prev) return;
    const delta = count - prev;
    const id = nextIdRef.current++;
    setBursts((b) => [...b, { id, delta }]);
    const timer = window.setTimeout(() => {
      setBursts((b) => b.filter((x) => x.id !== id));
    }, 900);
    return () => window.clearTimeout(timer);
  }, [count]);

  return (
    <span
      aria-label={`Bonus pot ${count}`}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
      }}
    >
      <img
        src={iconUrl}
        alt=""
        aria-hidden
        style={{
          width: BONUS_ICON_SIZE,
          height: BONUS_ICON_SIZE,
          objectFit: 'contain',
          filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))',
        }}
      />
      <span
        style={{
          fontWeight: 900,
          fontSize: 16,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {count.toLocaleString()}
      </span>
      {bursts.map((b) => (
        <span
          key={b.id}
          aria-hidden
          style={{
            position: 'absolute',
            left: 0,
            top: -2,
            transform: 'translateY(0)',
            color: '#FFE48A',
            fontWeight: 900,
            fontSize: 13,
            textShadow:
              '0 1px 2px rgba(0,0,0,0.85), 0 0 8px rgba(255,228,138,0.7)',
            pointerEvents: 'none',
            animation: 'balance-pill-burst 900ms ease-out forwards',
            whiteSpace: 'nowrap',
          }}
        >
          +{b.delta}
        </span>
      ))}
      <style>{`
        @keyframes balance-pill-burst {
          0%   { opacity: 0; transform: translateY(4px) scale(0.85); }
          18%  { opacity: 1; transform: translateY(-2px) scale(1.08); }
          100% { opacity: 0; transform: translateY(-22px) scale(1);   }
        }
      `}</style>
    </span>
  );
}

/** Items chip — star glyph + total count of items collected this
 *  match. Status-only; deploy happens in the wheel's STAKE surface,
 *  not here. Pops a "+N" burst whenever the count grows so the
 *  player has a visible cue when a reel-race item drop lands. */
function ItemsChip({ count, iconUrl }: { count: number; iconUrl: string }) {
  const prevCountRef = useRef(count);
  const [bursts, setBursts] = useState<{ id: number; delta: number }[]>([]);
  const nextIdRef = useRef(0);
  useEffect(() => {
    const prev = prevCountRef.current;
    prevCountRef.current = count;
    if (count <= prev) return;
    const delta = count - prev;
    const id = nextIdRef.current++;
    setBursts((b) => [...b, { id, delta }]);
    const timer = window.setTimeout(() => {
      setBursts((b) => b.filter((x) => x.id !== id));
    }, 900);
    return () => window.clearTimeout(timer);
  }, [count]);

  return (
    <span
      data-balance-pill-items
      aria-label={`Bonus items ${count}`}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
      }}
    >
      <img
        src={iconUrl}
        alt=""
        aria-hidden
        style={{
          width: BONUS_ICON_SIZE,
          height: BONUS_ICON_SIZE,
          objectFit: 'contain',
          filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))',
        }}
      />
      <span
        style={{
          fontWeight: 900,
          fontSize: 16,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {count.toLocaleString()}
      </span>
      {bursts.map((b) => (
        <span
          key={b.id}
          aria-hidden
          style={{
            position: 'absolute',
            left: 0,
            top: -2,
            transform: 'translateY(0)',
            color: '#FFE48A',
            fontWeight: 900,
            fontSize: 13,
            textShadow:
              '0 1px 2px rgba(0,0,0,0.85), 0 0 8px rgba(255,228,138,0.7)',
            pointerEvents: 'none',
            animation: 'balance-pill-burst 900ms ease-out forwards',
            whiteSpace: 'nowrap',
          }}
        >
          +{b.delta}
        </span>
      ))}
    </span>
  );
}

/** Small vertically-centred dot between the bonus cluster and the
 *  wallet cluster — calls out the divide without taking much width. */
function DotDivider() {
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-block',
        width: 4,
        height: 4,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.55)',
      }}
    />
  );
}
